import axios from 'axios';
import { SEAO_ADMINISTRATIVE_REGIONS } from '@/models/search-filters';
import { platformsService } from '@/services/platforms.service';
import { userPlatformsService } from '@/services/user-platforms.service';

const initialState = () => ({
  userPlatforms: [],
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const platformAccessId = (itemOrId) => {
  if (itemOrId == null) return '';
  const raw =
    typeof itemOrId === 'string' || typeof itemOrId === 'number' ? itemOrId : itemOrId.platformId || itemOrId.id;
  return String(raw || '')
    .toLowerCase()
    .replace(/-/g, '_');
};

const indexByAccessId = (list) => {
  const map = new Map();
  (list || []).forEach((item) => {
    const key = platformAccessId(item);
    if (key) map.set(key, item);
  });
  return map;
};

const actions = {
  getUserPlatforms({ commit }) {
    commit('GET_USER_PLATFORMS');
    // Tenant access is what admin toggles; the user endpoint can lag behind a newly
    // enabled catalog platform (canada_buys / AchatsCanada). Union both.
    return Promise.all([
      platformsService.getPlatforms(),
      userPlatformsService.getUserPlatforms(),
      platformsService.getTenantPlatforms().catch(() => []),
    ]).then(
      ([platforms, userAllowedPlatforms, tenantPlatforms]) =>
        commit('SUCCESS', { platforms, userAllowedPlatforms, tenantPlatforms }),
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
        }
      },
    );
  },
};

const mutations = {
  GET_USER_PLATFORMS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SUCCESS(state, { platforms, userAllowedPlatforms, tenantPlatforms }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const allowedById = indexByAccessId(userAllowedPlatforms);
    const tenantById = indexByAccessId(tenantPlatforms);
    state.userPlatforms = (platforms || [])
      .map((platform) => {
        const key = platformAccessId(platform);
        const allowed = allowedById.get(key);
        const tenant = tenantById.get(key);
        // User list is fetched with hasAccessOnly=true, so presence counts as access
        // even when the payload omits hasAccess. Tenant toggle covers catalog rows
        // the user endpoint has not caught up with yet.
        const hasAccess = allowed ? allowed.hasAccess !== false : !!(tenant && tenant.hasAccess);
        if (!hasAccess) return null;
        return {
          ...tenant,
          ...allowed,
          ...(key === 'seao_v2' ? { regions: Object.keys(SEAO_ADMINISTRATIVE_REGIONS) } : {}),
          ...platform,
          id: platform.id,
          hasAccess: true,
        };
      })
      .filter(Boolean);
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
    state.userPlatforms = [];
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  userPlatforms: (state) => state.userPlatforms,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const userPlatforms = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
